import React, { useState, useEffect, useCallback } from 'react';
import { CalendarBlank, ArrowLeft, ArrowRight, Gear, Robot, Buildings, Users, CheckCircle, Clock, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PRI_CLR = {
  Urgent: 'bg-[#FFEBEE] text-[#B71C1C]',
  Require: 'bg-[#FFF3E0] text-[#E65100]',
  Normal: 'bg-[#E3F2FD] text-[#0D47A1]',
  Medium: 'bg-[#E3F2FD] text-[#0D47A1]',
  High: 'bg-[#FFF3E0] text-[#E65100]',
  Low: 'bg-[#F3E5F5] text-[#6A1B9A]',
};

function fmt(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}

function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function AdminScheduleView() {
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [days, setDays] = useState(7);
  const [schedule, setSchedule] = useState([]);
  const [maxPerDay, setMaxPerDay] = useState(7);
  const [newLimit, setNewLimit] = useState(7);
  const [loading, setLoading] = useState(false);
  const [settingLoading, setSettingLoading] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [expandedCell, setExpandedCell] = useState(null); // "team|date"

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/service-tickets/team-schedule?date=${startDate}&days=${days}`, { headers });
      const data = await res.json();
      setSchedule(data.teams || []);
      setMaxPerDay(data.max_per_day || 7);
      setNewLimit(data.max_per_day || 7);
    } catch (e) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [startDate, days]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const saveSiteLimit = async () => {
    setSettingLoading(true);
    try {
      const res = await fetch(`${API}/admin/schedule-settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ max_sites_per_day: parseInt(newLimit) }),
      });
      const data = await res.json();
      if (data.max_sites_per_day) {
        toast.success(`Site limit updated to ${newLimit} sites/day`);
        setMaxPerDay(parseInt(newLimit));
        setShowSettings(false);
        loadSchedule();
      } else {
        toast.error(data.error || 'Failed to update setting');
      }
    } catch (e) {
      toast.error('Failed to save setting');
    } finally {
      setSettingLoading(false);
    }
  };

  const runAutoSchedule = async (dryRun = false) => {
    setAutoRunning(true);
    setAutoResult(null);
    try {
      const res = await fetch(`${API}/service-tickets/auto-schedule`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ start_date: startDate, dry_run: dryRun }),
      });
      const data = await res.json();
      setAutoResult({ ...data, dryRun });
      if (!dryRun) {
        toast.success(`Auto-scheduled ${data.scheduled} tickets`);
        loadSchedule();
      } else {
        toast.info(`[Dry Run] Would schedule ${data.scheduled} tickets`);
      }
    } catch (e) {
      toast.error('Auto-schedule failed');
    } finally {
      setAutoRunning(false);
    }
  };

  // Build date columns
  const dateColumns = [];
  for (let i = 0; i < days; i++) {
    dateColumns.push(addDays(startDate, i));
  }

  // Build summary: total tickets per date across all teams
  const dateTotals = {};
  dateColumns.forEach(d => { dateTotals[d] = 0; });
  schedule.forEach(team => {
    (team.days || []).forEach(day => {
      if (dateTotals[day.date] !== undefined) dateTotals[day.date] += day.count;
    });
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-[18px] font-bold flex items-center gap-2">
            <CalendarBlank size={22} className="text-[var(--md-primary)]" weight="fill" />
            Daily Schedule View
          </h2>
          <p className="text-[12px] text-[var(--md-on-surface-variant)] mt-0.5">
            Team workload by date — max <strong>{maxPerDay} sites/day</strong> per team
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--md-outline-variant)] text-[12px] hover:bg-[var(--md-surface-1)] transition-colors"
          >
            <Gear size={15} /> Settings
          </button>
          <button
            onClick={() => runAutoSchedule(true)}
            disabled={autoRunning}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#0D47A1] text-[#0D47A1] text-[12px] hover:bg-[#E3F2FD] transition-colors"
          >
            <Robot size={15} /> Dry Run
          </button>
          <button
            onClick={() => runAutoSchedule(false)}
            disabled={autoRunning}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[var(--md-primary)] text-white text-[12px] hover:bg-[var(--md-primary)]/90 transition-colors"
          >
            <Robot size={15} /> {autoRunning ? 'Running…' : 'Auto-Schedule Now'}
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="border-2 border-[var(--md-primary)] rounded-xl p-4 bg-[var(--md-primary-container)]">
          <h3 className="text-[14px] font-bold mb-3 flex items-center gap-2">
            <Gear size={16} /> Schedule Settings
          </h3>
          <div className="flex items-end gap-3">
            <div>
              <label className="text-[12px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">
                Max Sites Per Team Per Day
              </label>
              <input
                type="number"
                min={1}
                max={20}
                value={newLimit}
                onChange={e => setNewLimit(e.target.value)}
                className="md3-input h-10 w-24 text-center text-[16px] font-bold"
              />
            </div>
            <button
              onClick={saveSiteLimit}
              disabled={settingLoading}
              className="px-4 py-2 rounded-lg bg-[var(--md-primary)] text-white text-[13px] font-medium h-10"
            >
              {settingLoading ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setShowSettings(false)} className="px-4 py-2 rounded-lg border border-[var(--md-outline-variant)] text-[13px] h-10">
              Cancel
            </button>
          </div>
          <p className="text-[11px] text-[var(--md-on-surface-variant)] mt-2">
            When a team reaches this limit on a given day, new tickets will be pushed to the next available day.
          </p>
        </div>
      )}

      {/* Auto-schedule result */}
      {autoResult && (
        <div className={`rounded-xl p-4 border ${autoResult.dryRun ? 'border-[#0D47A1] bg-[#E3F2FD]' : 'border-[#2E7D32] bg-[#E8F5E9]'}`}>
          <div className="flex items-center justify-between mb-2">
            <p className={`text-[13px] font-bold ${autoResult.dryRun ? 'text-[#0D47A1]' : 'text-[#2E7D32]'}`}>
              {autoResult.dryRun ? '🔍 Dry Run Result' : '✅ Auto-Schedule Complete'}
            </p>
            <button onClick={() => setAutoResult(null)} className="text-[11px] text-[var(--md-on-surface-variant)] underline">Dismiss</button>
          </div>
          <p className="text-[12px]">{autoResult.message}</p>
          {autoResult.assignments?.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[var(--md-on-surface-variant)]">
                    <th className="text-left py-1">Ticket</th>
                    <th className="text-left py-1">Team</th>
                    <th className="text-left py-1">Township</th>
                    <th className="text-left py-1">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {autoResult.assignments.slice(0, 50).map((a, i) => (
                    <tr key={i} className="border-t border-[var(--md-outline-variant)]/30">
                      <td className="py-1 font-mono">{a.ticket_id}</td>
                      <td className="py-1">{a.team}</td>
                      <td className="py-1">{a.township || '—'}</td>
                      <td className="py-1">{a.scheduled_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {autoResult.assignments.length > 50 && (
                <p className="text-[10px] text-[var(--md-on-surface-variant)] mt-1">…and {autoResult.assignments.length - 50} more</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Date navigation */}
      <div className="flex items-center gap-3">
        <button onClick={() => setStartDate(addDays(startDate, -7))} className="p-2 rounded-lg border border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)]">
          <ArrowLeft size={16} />
        </button>
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          className="md3-input h-9 text-[13px] w-36"
        />
        <select
          value={days}
          onChange={e => setDays(parseInt(e.target.value))}
          className="md3-input h-9 text-[13px] w-28"
        >
          <option value={3}>3 days</option>
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
        </select>
        <button onClick={() => setStartDate(addDays(startDate, 7))} className="p-2 rounded-lg border border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)]">
          <ArrowRight size={16} />
        </button>
        <button onClick={() => setStartDate(new Date().toISOString().slice(0, 10))} className="px-3 py-1.5 rounded-lg border border-[var(--md-outline-variant)] text-[12px] hover:bg-[var(--md-surface-1)]">
          Today
        </button>
        <button onClick={loadSchedule} disabled={loading} className="px-3 py-1.5 rounded-lg border border-[var(--md-outline-variant)] text-[12px] hover:bg-[var(--md-surface-1)]">
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      {/* Summary row: total tickets per date */}
      <div className="grid gap-2" style={{ gridTemplateColumns: `160px repeat(${days}, 1fr)` }}>
        <div className="text-[11px] font-bold text-[var(--md-on-surface-variant)] flex items-center gap-1">
          <Buildings size={14} /> Township / Team
        </div>
        {dateColumns.map(d => (
          <div key={d} className={`text-center rounded-lg py-1.5 ${d === new Date().toISOString().slice(0,10) ? 'bg-[var(--md-primary)] text-white' : 'bg-[var(--md-surface-1)]'}`}>
            <p className="text-[10px] font-bold">{fmt(d)}</p>
            <p className="text-[11px]">{dateTotals[d] || 0} tickets</p>
          </div>
        ))}
      </div>

      {/* Per-team rows */}
      {loading ? (
        <div className="text-center py-10 text-[var(--md-on-surface-variant)] text-[13px]">Loading schedule…</div>
      ) : schedule.length === 0 ? (
        <div className="text-center py-10 text-[var(--md-on-surface-variant)] text-[13px]">No scheduled tickets in this period.</div>
      ) : (
        <div className="space-y-2">
          {schedule.map(team => {
            // Build day map
            const dayMap = {};
            (team.days || []).forEach(d => { dayMap[d.date] = d; });

            // Get unique townships for this team in this period
            const townships = new Set();
            (team.days || []).forEach(d => {
              (d.tickets || []).forEach(t => { if (t.township) townships.add(t.township); });
            });

            return (
              <div key={team.team} className="border border-[var(--md-outline-variant)] rounded-xl overflow-hidden">
                {/* Team header row */}
                <div
                  className="grid items-center cursor-pointer hover:bg-[var(--md-surface-1)] transition-colors"
                  style={{ gridTemplateColumns: `160px repeat(${days}, 1fr)` }}
                  onClick={() => setExpandedTeam(expandedTeam === team.team ? null : team.team)}
                >
                  <div className="px-3 py-2.5 flex items-center gap-2">
                    <Users size={14} className="text-[var(--md-primary)] flex-shrink-0" />
                    <div>
                      <p className="text-[12px] font-bold leading-tight">{team.team}</p>
                      {townships.size > 0 && (
                        <p className="text-[9px] text-[var(--md-on-surface-variant)] leading-tight">{[...townships].slice(0,3).join(', ')}{townships.size > 3 ? ` +${townships.size-3}` : ''}</p>
                      )}
                    </div>
                  </div>
                  {dateColumns.map(d => {
                    const day = dayMap[d];
                    const count = day?.count || 0;
                    const full = count >= maxPerDay;
                    const cellKey = `${team.team}|${d}`;
                    return (
                      <div
                        key={d}
                        className={`px-2 py-2.5 text-center border-l border-[var(--md-outline-variant)]/30 cursor-pointer hover:bg-[var(--md-surface-2)] transition-colors ${full ? 'bg-[#FFEBEE]' : count > 0 ? 'bg-[#E8F5E9]/50' : ''}`}
                        onClick={e => { e.stopPropagation(); setExpandedCell(expandedCell === cellKey ? null : cellKey); }}
                      >
                        {count > 0 ? (
                          <>
                            <p className={`text-[13px] font-bold ${full ? 'text-[#B71C1C]' : 'text-[#2E7D32]'}`}>{count}</p>
                            <p className="text-[9px] text-[var(--md-on-surface-variant)]">{full ? '🔴 Full' : `${maxPerDay - count} left`}</p>
                          </>
                        ) : (
                          <p className="text-[11px] text-[var(--md-outline)]">—</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Expanded cell: ticket list for specific team+date */}
                {dateColumns.map(d => {
                  const cellKey = `${team.team}|${d}`;
                  if (expandedCell !== cellKey) return null;
                  const day = dayMap[d];
                  if (!day?.tickets?.length) return null;
                  return (
                    <div key={`expand-${cellKey}`} className="border-t border-[var(--md-outline-variant)] bg-[var(--md-surface-1)] px-4 py-3">
                      <p className="text-[11px] font-bold mb-2 text-[var(--md-on-surface-variant)]">
                        {team.team} — {fmt(d)} ({day.tickets.length} tickets)
                      </p>
                      <div className="space-y-1.5">
                        {day.tickets.map(t => (
                          <div key={t.ticket_id} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-[var(--md-outline-variant)]/50 text-[11px]">
                            <span className="font-mono text-[10px] text-[var(--md-on-surface-variant)] w-20 flex-shrink-0">{t.ticket_id}</span>
                            <span className="flex-1 font-medium truncate">{t.customer_name || t.customer_id}</span>
                            {t.township && <span className="text-[10px] bg-[#E3F2FD] text-[#0D47A1] px-1.5 py-0.5 rounded">{t.township}</span>}
                            {t.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${PRI_CLR[t.priority] || 'bg-gray-100 text-gray-600'}`}>{t.priority}</span>}
                            <span className="text-[10px] text-[var(--md-on-surface-variant)]">{t.issue_type}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Expanded team: township breakdown */}
                {expandedTeam === team.team && (
                  <div className="border-t border-[var(--md-outline-variant)] bg-[var(--md-surface-1)] px-4 py-3">
                    <p className="text-[11px] font-bold mb-2 text-[var(--md-on-surface-variant)]">Township breakdown for {team.team}</p>
                    {(() => {
                      // Aggregate by township across all days
                      const twMap = {};
                      (team.days || []).forEach(day => {
                        (day.tickets || []).forEach(t => {
                          const tw = t.township || 'Unknown';
                          if (!twMap[tw]) twMap[tw] = { count: 0, dates: new Set() };
                          twMap[tw].count++;
                          twMap[tw].dates.add(day.date);
                        });
                      });
                      const entries = Object.entries(twMap).sort((a, b) => b[1].count - a[1].count);
                      if (!entries.length) return <p className="text-[11px] text-[var(--md-on-surface-variant)]">No data</p>;
                      return (
                        <div className="flex flex-wrap gap-2">
                          {entries.map(([tw, info]) => (
                            <div key={tw} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-[var(--md-outline-variant)]/50 text-[11px]">
                              <Buildings size={12} className="text-[var(--md-primary)]" />
                              <span className="font-medium">{tw}</span>
                              <span className="text-[var(--md-on-surface-variant)]">{info.count} tickets · {info.dates.size} days</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
